import { useState } from 'react';
import axios from 'axios';
import { updateProfileRequest, updatePasswordRequest, saveAuth, getToken } from '../../utils/auth';
import { LANGUAGES, t } from '../../utils/i18n';
import useLang from '../../hooks/useLang';

const api = axios.create({ baseURL: '/api', timeout: 30000 });
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const inputStyle = { width: '100%', height: 40, fontSize: 14 };
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 };

function Alert({ type, msg }) {
  if (!msg) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? 'var(--red-dim)' : 'var(--green-dim)',
      border: `1px solid ${isError ? 'var(--red)' : 'var(--green)'}`,
      borderRadius: 6, padding: '10px 14px', fontSize: 13,
      color: isError ? 'var(--red)' : 'var(--green)', marginBottom: 16,
    }}>{msg}</div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function DataBackupSection() {
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [importing, setImporting] = useState(false);

  const handleExport = async () => {
    try {
      setStatus({ type: '', msg: '' });
      const [watchlistRes, alertsRes, portfolioRes, accountsRes] = await Promise.all([
        api.get('/watchlist', auth()),
        api.get('/alerts', auth()),
        api.get('/portfolio', auth()),
        api.get('/portfolio/accounts', auth()),
      ]);
      const accMap = {};
      accountsRes.data.forEach(a => { accMap[a.id] = a.name; });
      const holdingsWithAccName = portfolioRes.data.map(h => ({
        ...h, account_name: accMap[h.account_id] || 'Default',
      }));
      const backup = {
        version: 1,
        exportedAt: new Date().toISOString(),
        watchlist: watchlistRes.data,
        alerts: alertsRes.data,
        portfolio: { accounts: accountsRes.data, holdings: holdingsWithAccName },
      };
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `freejini-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setStatus({ type: 'success', msg: 'Backup downloaded successfully.' });
    } catch {
      setStatus({ type: 'error', msg: 'Export failed. Please try again.' });
    }
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async (ev) => {
      setImporting(true);
      setStatus({ type: '', msg: '' });
      try {
        const data = JSON.parse(ev.target.result);
        if (!data.version) throw new Error('Invalid backup file.');

        // Restore watchlist
        if (Array.isArray(data.watchlist)) await api.put('/watchlist', { symbols: data.watchlist }, auth());

        // Restore alerts
        if (Array.isArray(data.alerts)) {
          for (const alert of data.alerts) {
            await api.post('/alerts', {
              symbol: alert.symbol, condition: alert.condition,
              target_price: alert.target_price, note: alert.note,
            }, auth()).catch(() => {});
          }
        }

        // Restore portfolio accounts & holdings
        if (data.portfolio) {
          const { accounts = [], holdings = [] } = data.portfolio;
          for (const acc of accounts) {
            await api.post('/portfolio/accounts', { name: acc.name, broker: acc.broker, currency: acc.currency }, auth()).catch(() => {});
          }
          // Re-fetch accounts to map names → new IDs
          const accRes = await api.get('/portfolio/accounts', auth());
          const accMap = {};
          accRes.data.forEach(a => { accMap[a.name] = a.id; });
          for (const h of holdings) {
            const accountId = accMap[h.account_name] || accMap[Object.keys(accMap)[0]];
            if (!accountId) continue;
            await api.post('/portfolio', {
              account_id: accountId, symbol: h.symbol, shares: h.shares,
              avg_cost: h.avg_cost, currency: h.currency, notes: h.notes,
            }, auth()).catch(() => {});
          }
        }

        window.dispatchEvent(new Event('watchlist-update'));
        setStatus({ type: 'success', msg: 'Data imported successfully. Refresh to see all changes.' });
      } catch {
        setStatus({ type: 'error', msg: 'Import failed. Make sure you selected a valid Freejini backup file.' });
      } finally { setImporting(false); }
    };
    reader.readAsText(file);
  };

  return (
    <Section title="Data & Backup">
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
        Export your watchlist, alerts, and portfolio to a JSON file. Import to restore on any device.
      </div>
      {status.msg && (
        <div style={{
          background: status.type === 'error' ? 'var(--red-dim)' : 'var(--green-dim)',
          border: `1px solid ${status.type === 'error' ? 'var(--red)' : 'var(--green)'}`,
          borderRadius: 6, padding: '10px 14px', fontSize: 13,
          color: status.type === 'error' ? 'var(--red)' : 'var(--green)', marginBottom: 14,
        }}>{status.msg}</div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-ghost" onClick={handleExport} style={{ fontSize: 13, height: 38, flex: 1 }}>
          ↓ Export Backup
        </button>
        <label style={{ flex: 1 }}>
          <input type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} disabled={importing} />
          <span className="btn btn-ghost" style={{
            fontSize: 13, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: importing ? 'not-allowed' : 'pointer', opacity: importing ? 0.6 : 1,
          }}>
            {importing ? 'Importing...' : '↑ Import Backup'}
          </span>
        </label>
      </div>
    </Section>
  );
}

export default function ProfileModal({ user, theme, onThemeToggle, onClose, onUserUpdate, onLogout }) {
  const { lang, changeLang } = useLang();
  // Profile form
  const [profile, setProfile] = useState({ name: user.name, email: user.email });
  const [profileStatus, setProfileStatus] = useState({ type: '', msg: '' });
  const [profileLoading, setProfileLoading] = useState(false);

  // Password form
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwStatus, setPwStatus] = useState({ type: '', msg: '' });
  const [pwLoading, setPwLoading] = useState(false);

  const setP = (k) => (e) => setProfile(f => ({ ...f, [k]: e.target.value }));
  const setPw_ = (k) => (e) => setPw(f => ({ ...f, [k]: e.target.value }));

  const submitProfile = async (e) => {
    e.preventDefault();
    if (!profile.name.trim() || !profile.email.trim()) return setProfileStatus({ type: 'error', msg: 'Name and email are required.' });
    setProfileLoading(true);
    setProfileStatus({ type: '', msg: '' });
    try {
      const data = await updateProfileRequest(profile.name.trim(), profile.email.trim());
      saveAuth(data.token, data.user);
      onUserUpdate(data.user);
      setProfileStatus({ type: 'success', msg: 'Profile updated successfully.' });
    } catch (err) {
      setProfileStatus({ type: 'error', msg: err.response?.data?.error || 'Failed to update profile.' });
    } finally { setProfileLoading(false); }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) return setPwStatus({ type: 'error', msg: 'New passwords do not match.' });
    if (pw.next.length < 6) return setPwStatus({ type: 'error', msg: 'New password must be at least 6 characters.' });
    setPwLoading(true);
    setPwStatus({ type: '', msg: '' });
    try {
      await updatePasswordRequest(pw.current, pw.next);
      setPwStatus({ type: 'success', msg: 'Password changed successfully.' });
      setPw({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPwStatus({ type: 'error', msg: err.response?.data?.error || 'Failed to change password.' });
    } finally { setPwLoading(false); }
  };

  // Avatar initials
  const initials = user.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    // Backdrop
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, width: '100%', maxWidth: 480,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{t('accountSettings', lang)}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
        </div>

        <div style={{ padding: '24px' }}>
          {/* Avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'var(--accent-dim)', border: '2px solid var(--accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, fontWeight: 700, color: 'var(--accent)', flexShrink: 0,
            }}>{initials}</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)' }}>{user.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{user.email}</div>
            </div>
          </div>

          {/* Profile Section */}
          <Section title={t('profileInfo', lang)}>
            <form onSubmit={submitProfile}>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>{t('fullName', lang)}</label>
                <input type="text" className="input" value={profile.name} onChange={setP('name')} style={inputStyle} required />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>{t('emailAddress', lang)}</label>
                <input type="email" className="input" value={profile.email} onChange={setP('email')} style={inputStyle} required />
              </div>
              <Alert type={profileStatus.type} msg={profileStatus.msg} />
              <button type="submit" className="btn btn-primary" disabled={profileLoading} style={{ fontSize: 13, height: 38 }}>
                {profileLoading ? '...' : t('saveProfile', lang)}
              </button>
            </form>
          </Section>

          {/* Password Section */}
          <Section title={t('changePassword', lang)}>
            <form onSubmit={submitPassword}>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>{t('currentPassword', lang)}</label>
                <input type="password" className="input" value={pw.current} onChange={setPw_('current')} placeholder="••••••••" style={inputStyle} required />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>{t('newPassword', lang)}</label>
                <input type="password" className="input" value={pw.next} onChange={setPw_('next')} style={inputStyle} required />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>{t('confirmPassword', lang)}</label>
                <input type="password" className="input" value={pw.confirm} onChange={setPw_('confirm')} style={inputStyle} required />
              </div>
              <Alert type={pwStatus.type} msg={pwStatus.msg} />
              <button type="submit" className="btn btn-primary" disabled={pwLoading} style={{ fontSize: 13, height: 38 }}>
                {pwLoading ? '...' : t('changePassword', lang)}
              </button>
            </form>
          </Section>

          {/* Appearance */}
          <Section title={t('appearance', lang)}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                  {theme === 'dark' ? '🌙 Dark Mode' : '☀️ Light Mode'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                </div>
              </div>
              {/* Toggle switch */}
              <button
                onClick={onThemeToggle}
                style={{
                  position: 'relative', width: 48, height: 26, borderRadius: 13,
                  background: theme === 'dark' ? 'var(--accent)' : 'var(--border)',
                  border: 'none', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <span style={{
                  position: 'absolute', top: 3, width: 20, height: 20, borderRadius: '50%',
                  background: '#fff', transition: 'left 0.2s',
                  left: theme === 'dark' ? 25 : 3,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }} />
              </button>
            </div>

            {/* Theme preview cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16 }}>
              {[
                { label: 'Dark', value: 'dark', bg: '#111318', card: '#161921', text: '#e8eaf0', border: '#2a2d36' },
                { label: 'Light', value: 'light', bg: '#f0f2f5', card: '#ffffff', text: '#111827', border: '#d1d5db' },
              ].map(t => (
                <button
                  key={t.value}
                  onClick={() => t.value !== theme && onThemeToggle()}
                  style={{
                    background: t.bg, border: `2px solid ${theme === t.value ? 'var(--accent)' : t.border}`,
                    borderRadius: 8, padding: 10, cursor: 'pointer', textAlign: 'left',
                    transition: 'border-color 0.15s',
                  }}
                >
                  <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 4, padding: '6px 8px', marginBottom: 6 }}>
                    <div style={{ width: '60%', height: 4, background: '#3b82f6', borderRadius: 2, marginBottom: 4 }} />
                    <div style={{ width: '40%', height: 3, background: t.border, borderRadius: 2 }} />
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: t.text }}>{t.label}</div>
                  {theme === t.value && (
                    <div style={{ fontSize: 10, color: '#3b82f6', marginTop: 2 }}>Active</div>
                  )}
                </button>
              ))}
            </div>
          </Section>

          {/* Language */}
          <Section title={t('language', lang)}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {LANGUAGES.map(l => {
                const active = lang === l.code;
                return (
                  <button
                    key={l.code}
                    onClick={() => changeLang(l.code)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                      padding: '14px 10px', borderRadius: 8, cursor: 'pointer',
                      border: `2px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                      background: active ? 'var(--accent-dim)' : 'var(--bg-surface)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <span style={{ fontSize: 26 }}>{l.flag}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: active ? 'var(--accent)' : 'var(--text-primary)' }}>
                      {l.label}
                    </span>
                    {active && (
                      <span style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 700, letterSpacing: '0.04em' }}>
                        ✓ Active
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Data & Backup */}
          <DataBackupSection />

          {/* Danger Zone */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Session</div>
            <button
              onClick={onLogout}
              className="btn btn-ghost"
              style={{ fontSize: 13, height: 36, color: 'var(--red)', borderColor: 'var(--red)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--red-dim)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              {t('signOut', lang)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
